// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaDay,
  $FullRegattaProtestEnd,
  $Model,
} from 'common.data';
import type {
  $DateTimeFormats,
} from 'common.helpers';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';
import {
  RegattaStatus,
} from 'common.data';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getDateTimeFormats,
} from 'common.helpers/dist/browser';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import RegattaProtestDialog from '../../../dialog/RegattaProtestDialog';
import LabelQuantity from '../../../common/LabelQuantity';
import NoData from '../../../common/NoData';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $Model<'Regatta'>;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: {
    RegattaDays: Array<$FullRegattaDay>;
    RegattaProtests: Array<$Model<'RegattaProtest'>>;
  };
  loading: boolean;
};

class ProtestsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: {
        RegattaDays: [],
        RegattaProtests: [],
      },
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaProtestsSectionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id
          && (data.modelName === 'RegattaProtest' || data.modelName === 'RegattaProtestEnd')
        ) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  getRegattaDayRegattaProtests({
    end,
    start,
  }: {
    end: moment.Moment | null;
    start: moment.Moment | null;
  }): Array<$Model<'RegattaProtest'>> {
    const {
      data,
    } = this.state;

    let filteredData = data.RegattaProtests;

    if (start !== null) {
      filteredData = filteredData.filter((
        dataItem: $Model<'RegattaProtest'>,
      ) => moment(dataItem.RaceDate).isSameOrAfter(start));
    }

    if (end !== null) {
      filteredData = filteredData.filter((
        dataItem: $Model<'RegattaProtest'>,
      ) => moment(dataItem.RaceDate).isBefore(end));
    }

    return filteredData;
  }

  hasData(): boolean {
    const {
      data,
    } = this.state;

    const regattaDaysWithRegattaProtestEnds = data.RegattaDays.filter(({
      RegattaProtestEnds,
    }) => RegattaProtestEnds.length > 0);

    return (data.RegattaProtests.length > 0 || regattaDaysWithRegattaProtestEnds.length > 0);
  }

  renderRegattaProtestList({
    data,
    formats,
  }: {
    data: Array<$Model<'RegattaProtest'>>;
    formats: $DateTimeFormats;
  }) {
    const {
      t,
    } = this.props;

    const dateTimeFormat = `${formats.Date} ${formats.Time}`;

    return (data.map((dataItem: $Model<'RegattaProtest'>) => (
      <tr
        key={dataItem.id}
        title={t('Protest')}
      >
        <td>
          {`#${dataItem.CaseNo || t('TBD')}`}
        </td>
        <td className={'whitespace-nowrap'}>
          <span>{dataItem.RaceDate}</span>
          {dataItem.RaceNo && (
            <span>{` / ${dataItem.RaceNo || ''}`}</span>
          )}
        </td>
        <td>
          {dataItem.Protestor}
        </td>
        <td>
          {dataItem.Protestee}
        </td>
        <td>
          {dataItem.Rule}
        </td>
        <td className={'whitespace-nowrap'}>
          {dataItem.HearingDate === null
            ? t('Will be defined')
            : moment(dataItem.HearingDate).format(dateTimeFormat)}
        </td>
        <td>
          {dataItem.Decision}
        </td>
      </tr>
    )));
  }

  renderRegattaProtestEndList({
    data,
    formats,
  }: {
    data: Array<$FullRegattaProtestEnd>;
    formats: $DateTimeFormats;
  }) {
    const {
      t,
    } = this.props;

    return (data.map((dataItem: $FullRegattaProtestEnd) => (
      <div
        className={'badge badge_outlined badge_secondary badge_gray disabled uppercase whitespace-nowrap mr-1 mb-1'}
        key={dataItem.id}
        title={t('Group')}
      >
        <span className={'mr-1'}>
          {`${dataItem.RegattaResultTable.Name} |`}
        </span>
        <span className={'text-black'}>
          {moment(dataItem.EndDate).format(formats.Time)}
        </span>
      </div>
    )));
  }

  renderRegattaDayListItem({
    dataItem,
    formats,
    index,
  }: {
    dataItem: $FullRegattaDay;
    formats: $DateTimeFormats;
    index: number;
  }) {
    const {
      t,
    } = this.props;

    let labelString = `${t('Regatta day')} #${index + 1} (${dataItem.NameDate})`;

    if (dataItem.RegattaProtestEnds.length > 0) {
      labelString = `${labelString} ${t('protest time limit')}: `;
    }

    return (
      <tr key={dataItem.id}>
        <th
          className={'py-1-i'}
          colSpan={7}
        >
          <div className={'w-full text-left'}>
            <div className={'font-bold mr-3 mb-1'}>
              {labelString}
            </div>
            {this.renderRegattaProtestEndList({
              data: dataItem.RegattaProtestEnds,
              formats,
            })}
          </div>
        </th>
      </tr>
    );
  }

  renderRegattaDayList({
    formats,
  }: {
    formats: $DateTimeFormats;
  }) {
    const {
      data,
    } = this.state;

    return (data.RegattaDays.map((dataItem: $FullRegattaDay, index: number) => {
      const start: moment.Moment = moment(dataItem.NameDate);
      const end: moment.Moment | null = index === data.RegattaDays.length - 1
        ? null
        : moment(start).add(
          1,
          'day',
        );

      const regattaProtests: Array<$Model<'RegattaProtest'>> = this.getRegattaDayRegattaProtests({
        end,
        start,
      });

      const show: boolean = _.sum([
        regattaProtests.length > 0,
        dataItem.RegattaProtestEnds.length > 0,
      ]) > 0;

      return (
        <React.Fragment key={dataItem.id}>
          {index === 0 && this.renderRegattaProtestList({
            data: this.getRegattaDayRegattaProtests({
              end: start,
              start: null,
            }),
            formats,
          })}
          {show === true && this.renderRegattaDayListItem({
            dataItem,
            formats,
            index,
          })}
          {this.renderRegattaProtestList({
            data: regattaProtests,
            formats,
          })}
        </React.Fragment>
      );
    }));
  }

  renderTable() {
    const {
      main,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const formats: $DateTimeFormats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    return (
      <table className={'table table_hoverable w-full'}>
        <thead>
          <tr>
            <th className={'text-left uppercase'}>
              {t('Case no.')}
            </th>
            <th className={'text-left uppercase'}>
              {`${t('Date')} / ${t('Race')}`}
            </th>
            <th className={'text-left uppercase'}>
              {t('Protestor')}
            </th>
            <th className={'text-left uppercase'}>
              {t('Protestee')}
            </th>
            <th className={'text-left uppercase'}>
              {t('Rule')}
            </th>
            <th className={'text-left uppercase'}>
              {t('Hearing date')}
            </th>
            <th className={'uppercase'}>
              <div className={'flex justify-between'}>
                <span>{t('Decision')}</span>
                <span>
                  <LabelQuantity value={data.RegattaProtests.length} />
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {this.renderRegattaDayList({
            formats,
          })}
        </tbody>
      </table>
    );
  }

  renderSubmitProtestButton() {
    const {
      item,
      t,
    } = this.props;

    return (
      <RegattaProtestDialog item={item}>
        <button
          className={'btn btn_primary btn_outlined uppercase whitespace-nowrap mb-3'}
          disabled={item.Status !== RegattaStatus.ongoing}
          type={'button'}
        >
          {t('Submit protest')}
        </button>
      </RegattaProtestDialog>
    );
  }

  render() {
    const {
      item,
      t,
    } = this.props;
    const {
      loading,
    } = this.state;

    const hasData: boolean = this.hasData();
    const showSection: boolean = item.Status === RegattaStatus.ongoing || hasData === true;

    return (showSection === true ? (
      <Section id={webSections[webPages.regattaPage].protests}>
        <div className={'flex flex-row mb-3'}>
          <h2 className={'h3 grow flex'}>
            {t('Protests')}
          </h2>
          {item.SubmitProtestEnabled === true && (
            <div>
              {this.renderSubmitProtestButton()}
            </div>
          )}
        </div>
        <Loader
          className={'overflow-x-auto'}
          fade
          loading={loading === true}
        >
          {hasData === true ? this.renderTable() : (
            <NoData label={t('No protests yet')} />
          )}
        </Loader>
      </Section>
    ) : null);
  }
}

export default ProtestsSectionView;
